import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import { Field, Form } from 'redux-form';
import moment from 'moment';
import { addCommas, formatDollar, getOrderStatus } from '../../utils/common';
import { SIMPLE_DATE_FORMAT } from '../../config/app_config';
import DialogForm from '../shared/DialogForm';
import InputField from '../shared/InputField';
import { checkButtonDisabled } from '../../utils/forms';

const EDIT_ORDER_DIALOG = 'editOrderDialog';
const RESET_INSTALL_DIALOG = 'resetInstallDialog';
const RESEND_INSTALL_EMAIL_DIALOG = 'resendInstallEmailDialog';

export default class ViewOrder extends React.Component {
  static propTypes = {
    cancelUpdateOrderFunction: PropTypes.func.isRequired,
    closeDialogFunction: PropTypes.func.isRequired,
    creditsRemaining: PropTypes.number,
    dialog: PropTypes.object.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    hasEditPerms: PropTypes.bool.isRequired,
    invalid: PropTypes.bool.isRequired,
    isSuperAdmin: PropTypes.bool.isRequired,
    install: PropTypes.object.isRequired,
    openDialogFunction: PropTypes.func.isRequired,
    order: PropTypes.object,
    pristine: PropTypes.bool.isRequired,
    skus: PropTypes.array,
    submitting: PropTypes.bool.isRequired,
    toggleIsBillable: PropTypes.func.isRequired,
    updateOrderFunction: PropTypes.func.isRequired,
    updateInstallFunction: PropTypes.func.isRequired,
    resendInstallEmailFunction: PropTypes.func.isRequired,
    useCases: PropTypes.object,
  };

  static defaultProps = {
    creditsRemaining: null,
    order: undefined,
    skus: undefined,
    useCases: undefined,
  };

  state = {
    updatedCredits: null,
    updatedNumberOfSeats: null,
    updatedEndDate: null,
    updatedEmailAddress: null,
    updatedCsvDownloadEnabled: null,
    updatedLeadMatchEnabled: null,
    updatedContactMatchByAccount: null,
    updatedPreferEndUserCredentials: null,
  };

  componentDidUpdate = () => {
    const {
      order,
      install: { customConfiguration },
      install: { customConfiguration: { csvDownloadEnabled, leadMatchEnabled, contactMatchByAccount, preferEndUserCredentials } },
    } = this.props;

    if (order) {
      const { recordCredits, numberOfSeats, endDate, person } = order;
      // Set credit state to initial credits on render
      if (recordCredits && this.state.updatedCredits === null) {
        this.setState({ updatedCredits: recordCredits });
      }
      if (numberOfSeats && this.state.updatedNumberOfSeats === null) {
        this.setState({ updatedNumberOfSeats: numberOfSeats });
      }
      if (endDate && this.state.updatedEndDate === null) {
        this.setState({ updatedEndDate: endDate });
      }
      if (person.email && this.state.updatedEmailAddress === null) {
        this.setState({ updatedEmailAddress: person.email });
      }
      if (customConfiguration) {
        if (csvDownloadEnabled !== null && this.state.updatedCsvDownloadEnabled === null) {
          this.setState({ updatedCsvDownloadEnabled: csvDownloadEnabled });
        }
        if (leadMatchEnabled !== null && this.state.updatedLeadMatchEnabled === null) {
          this.setState({ updatedLeadMatchEnabled: leadMatchEnabled });
        }
        if (contactMatchByAccount !== null && this.state.updatedContactMatchByAccount === null) {
          this.setState({ updatedContactMatchByAccount: contactMatchByAccount });
        }
        if (preferEndUserCredentials !== null && this.state.updatedPreferEndUserCredentials === null) {
          this.setState({ updatedPreferEndUserCredentials: preferEndUserCredentials });
        }
      }

      window.scrollTo(0, 0);
    }
  };

  toggleEditOrderDialog = () => {
    this.toggleDialog(EDIT_ORDER_DIALOG);
  };
  toggleResetInstallDialog = () => {
    this.toggleDialog(RESET_INSTALL_DIALOG);
  };
  toggleResendInstallEmailDialog = () => {
    this.toggleDialog(RESEND_INSTALL_EMAIL_DIALOG);
  };

  toggleDialog = id => {
    const { dialog, openDialogFunction, closeDialogFunction } = this.props;
    if (dialog.show) closeDialogFunction(id);
    else {
      const {
        order: { recordCredits, numberOfSeats, endDate, person },
        install: { customConfiguration: { csvDownloadEnabled, leadMatchEnabled, contactMatchByAccount, preferEndUserCredentials } },
      } = this.props;

      // Set credit state to initial credits on render
      if (recordCredits && this.state.updatedCredits !== recordCredits) {
        this.setState({ updatedCredits: recordCredits });
      }
      if (numberOfSeats && this.state.updatedNumberOfSeats !== numberOfSeats) {
        this.setState({ updatedNumberOfSeats: numberOfSeats });
      }
      if (endDate && this.state.updatedEndDate !== endDate) {
        this.setState({ updatedEndDate: endDate });
      }
      if (person.email && this.state.updatedEmailAddress !== person.email) {
        this.setState({ updatedEmailAddress: person.email });
      }
      if (csvDownloadEnabled !== null && this.state.updatedCsvDownloadEnabled !== csvDownloadEnabled) {
        this.setState({ updatedCsvDownloadEnabled: csvDownloadEnabled });
      }
      if (leadMatchEnabled !== null && this.state.updatedLeadMatchEnabled !== leadMatchEnabled) {
        this.setState({ updatedLeadMatchEnabled: leadMatchEnabled });
      }
      if (contactMatchByAccount !== null && this.state.updatedContactMatchByAccount !== contactMatchByAccount) {
        this.setState({ updatedContactMatchByAccount: contactMatchByAccount });
      }
      if (preferEndUserCredentials !== null && this.state.updatedPreferEndUserCredentials !== preferEndUserCredentials) {
        this.setState({ updatedPreferEndUserCredentials: preferEndUserCredentials });
      }

      openDialogFunction(id);
    }
  };

  leaveAndResetForm = evt => {
    const {
      cancelUpdateOrderFunction,
      order: { recordCredits, numberOfSeats, endDate, person },
      install: { customConfiguration: { csvDownloadEnabled, leadMatchEnabled, contactMatchByAccount, preferEndUserCredentials } },
    } = this.props;
    evt && evt.preventDefault();

    cancelUpdateOrderFunction();
    this.toggleDialog(EDIT_ORDER_DIALOG);
    this.setState({ updatedCredits: recordCredits });
    this.setState({ updatedNumberOfSeats: numberOfSeats });
    this.setState({ updatedEndDate: endDate });
    this.setState({ updatedEmailAddress: person.email });
    this.setState({ updatedCsvDownloadEnabled: csvDownloadEnabled});
    this.setState({ updatedLeadMatchEnabled: leadMatchEnabled});
    this.setState({ updatedContactMatchByAccount: contactMatchByAccount});
    this.setState({ updatedPreferEndUserCredentials: preferEndUserCredentials});
  };

  printPage = () => {
    window.print();
  };

  renderFieldAndLabel = (label, value) => (
    <div className={`${value} row`}>
      <div className="order-label col-md-4">
        <h6>{label}</h6>
      </div>
      <div className="order-value col-md-8">{value}</div>
    </div>
  );

  renderOptionalFieldAndLabel = (label, value) => {
    if (value === undefined) {
      return null;
    }
    return (
      <div className={`${value} row`}>
        <div className="order-label col-md-4">
          <h6>{label}:</h6>
        </div>
        <div className="order-value col-md-8">{value}</div>
      </div>
    );
  };

  renderFullWidthFieldAndLabel = (label, value) => (
    <div className={`${value} row`}>
      <div className="order-label col-xs-12">
        <h6>{label}</h6>
      </div>
      <div className="order-value col-xs-12">{value}</div>
    </div>
  );

  renderBoolean = bool => {
    if (bool) {
      return <span>Yes</span>;
    }
    return <span>No</span>;
  };

  renderBillingInfo = () => {
    const { isSuperAdmin, order, toggleIsBillable } = this.props;

    if (isSuperAdmin) {
      return (
        <span>
          <input
            name="isBillable"
            type="checkbox"
            checked={order.isBillable}
            onChange={() => toggleIsBillable()}
          />
          {!order.isBillable ? order.billingInfo : null}
        </span>
      );
    }

    return order.isBillable ? (
      <span>Yes</span>
    ) : (
      <span>No {order.billingInfo ? `- ${order.billingInfo}` : null}</span>
    );
  };

  renderCustomConfigurationRadio = (prop, label) => {
    let selected = String(this.state[prop]);
    return (
     <div>
     <label htmlFor={prop}>{label}
        <fieldset>
          <span id="radio-group" className="radio-inline">
            <Field
              name={prop}
              label="Yes"
              component={InputField}
              type="radio"
              value="true"
              checked={selected === "true"}
              onChange={evt => {
                 selected = evt.target.value;
                 this.setState({ [prop]: selected });
              }}
            />{' '}
          </span>
          <span className="radio-inline">
            <Field
              name={prop}
              label="No"
              component={InputField}
              type="radio"
              value="false"
              checked={selected === "false"}
              onChange={evt => {
                 selected = evt.target.value;
                 this.setState({ [prop]: selected });
              }}
            />{' '}
          </span>
         </fieldset>
       </label>
       </div>
    );
  };


  renderCustomerDetails = () => {
    const { order } = this.props;

    return (
      <div className="col-sm-6">
        <h6 className="text-uppercase text-muted">Customer</h6>
        <address>
          <strong>
            {order.company.name} (Customer #{order.providerCustomerNumber})
          </strong>
          <br />
          {order.company.addressLine1}
          <br />
          {order.company.addressLine2}
          {order.company.addressLine2 ? <br /> : null}
          {order.company.city} {order.company.state} {order.company.zipCode}
          <br />
          {order.company.country}
          <br />
          {order.company.phone}
        </address>
      </div>
    );
  };

  renderOrderDetails = () => {
    const { creditsRemaining, order } = this.props;
    const fullName = `${order.createdByFirstName} ${order.createdByLastName}`;
    return (
      <dl className="row dl-horizontal">
        <dt className="col-sm-6">Order</dt>
        <dd className="col-sm-6">{order.providerOrderNumber}</dd>

        <dt className="col-sm-6">Cost</dt>
        <dd className="col-sm-6">${addCommas(formatDollar(order.cost))}</dd>

        <dt className="col-sm-6">Billable</dt>
        <dd className="col-sm-6">{this.renderBillingInfo()}</dd>

        <dt className="col-sm-6">Seats</dt>
        <dd className="col-sm-6">{addCommas(order.numberOfSeats)}</dd>

        <dt className="col-sm-6">Initial Credits</dt>
        <dd className="col-sm-6">{addCommas(order.recordCredits)}</dd>

        <dt className="col-sm-6">Credits Remaining</dt>
        <dd className="col-sm-6">{addCommas(creditsRemaining)}</dd>

        <dt className="col-sm-6">Start Date</dt>
        <dd className="col-sm-6">{moment(order.startDate).format(SIMPLE_DATE_FORMAT)}</dd>

        <dt className="col-sm-6">End Date</dt>
        <dd className="col-sm-6">{moment(order.endDate).format(SIMPLE_DATE_FORMAT)}</dd>

        <dt className="col-sm-6">Created On</dt>
        <dd className="col-sm-6">{moment(order.createdOn).format(SIMPLE_DATE_FORMAT)}</dd>

        <dt className="col-sm-6">Created By</dt>
        <dd className="col-sm-6">{fullName}</dd>
      </dl>
    );
  };

  // assumes one SKU per order
  renderSkuFields = () => {
    const { skus, order, install } = this.props;
    return skus === undefined || skus.length === 0 ? (
      <p>No SKUs found</p>
    ) : (
      <dl className="row dl-horizontal">
        <dt className="col-sm-6">Dataset</dt>
        <dd className="col-sm-6">{skus[0].dataSet.name}</dd>

        <dt className="col-sm-6">SKU</dt>
        <dd className="col-sm-6">{skus[0].fieldSet.name}</dd>

        <dt className="col-sm-6">Total Fields</dt>
        <dd className="col-sm-6">{addCommas(skus[0].fieldSet.fieldCount)}</dd>

        <dt className="col-sm-6">Matched Pairs Provided</dt>
        <dd className="col-sm-6">{this.renderPrematchedValue(order)}</dd>

        <dt className="col-sm-6">CSV Download Enabled</dt>
        <dd className="col-sm-6">
          {this.renderBoolean(install.customConfiguration.csvDownloadEnabled)}
        </dd>

        <dt className="col-sm-6">Lead Match & Append Enabled</dt>
        <dd className="col-sm-6">
          {this.renderBoolean(install.customConfiguration.leadMatchEnabled)}
        </dd>

        {install.customConfiguration['acxiom.apiKey'] ? (
          <dt className="col-sm-6">Acxiom API Key</dt>
        ) : null}
        {install.customConfiguration['acxiom.apiKey'] ? (
          <dd className="col-sm-6">{install.customConfiguration['acxiom.apiKey']}</dd>
        ) : null}

        {install.customConfiguration['acxiom.apiSecret'] ? (
          <dt className="col-sm-6">Acxiom API Secret</dt>
        ) : null}
        {install.customConfiguration['acxiom.apiSecret'] ? (
          <dd className="col-sm-6">{install.customConfiguration['acxiom.apiSecret']}</dd>
        ) : null}

        <dt className="col-sm-6">Match Contacts within Matched Account Only</dt>
        <dd className="col-sm-6">
          {this.renderBoolean(install.customConfiguration.contactMatchByAccount)}
        </dd>

        <dt className="col-sm-6">Create Records Using Client Credentials</dt>
        <dd className="col-sm-6">
          {this.renderBoolean(install.customConfiguration.preferEndUserCredentials)}
        </dd>
      </dl>
    );
  };

  renderUseCases = () => {
    const { useCases } = this.props;
    const isPartialFile = useCases.listCriteria === 'Partial File';

    return useCases.length === 0 ? (
      <p>No use cases found</p>
    ) : (
      <dl className="row dl-horizontal">
        <dt className="col-sm-6">Platform</dt>
        <dd className="col-sm-6">{useCases.platform}</dd>

        <dt className="col-sm-6">Use Cases</dt>
        <dd className="col-sm-6">{useCases.cases}</dd>

        <dt className="col-sm-6">List Criteria</dt>
        <dd className="col-sm-6">{useCases.listCriteria}</dd>

        {isPartialFile && useCases.savedSearches ? (
          <dt className="col-sm-6">Search Parameters</dt>
        ) : null}
        {isPartialFile && useCases.savedSearches ? (
          <dd className="col-sm-6">{useCases.savedSearches.map(this.renderSearchCriteria)}</dd>
        ) : null}
      </dl>
    );
  };

  renderSearchCriteria = (criteria, index) => (
    <div key={index} className="search-criteria">
      <ul className="nav nav-tabs nav-tabs-dark">
        <li className="nav-item open">
          <strong className="nav-link">{criteria.recordLevel}</strong>
        </li>
      </ul>
      <div className="card card-block search-tab">
        {criteria.groups.map(this.renderSearchGroups)}
      </div>
    </div>
  );

  renderInstallationDetails = () => {
    const { install } = this.props;
    const isInstalled = install.installStatus === 'INSTALLED';

    return (
      <dl className="row dl-horizontal">
        <dt className="col-sm-6">Installation Key</dt>
        <dd className="col-sm-6">{install.installKey}</dd>

        <dt className="col-sm-6">Status</dt>
        <dd className="col-sm-6">{install.installStatus}</dd>

        <dt className="col-sm-6">Install Date</dt>
        <dd className="col-sm-6">
          {isInstalled ? moment(install.installDate).format(SIMPLE_DATE_FORMAT) : 'Not Installed'}
        </dd>
      </dl>
    );
  };

  renderSuperAdmin = () => {
    const { install, isSuperAdmin } = this.props;

    return isSuperAdmin ? (
      <dl className="row dl-horizontal">
        <dt className="col-sm-6">Installation ID</dt>
        <dd className="col-sm-6">{install.id}</dd>

        <dt className="col-sm-6">Description</dt>
        <dd className="col-sm-6">{install.description}</dd>

        <dt className="col-sm-6">Processing Status</dt>
        <dd className="col-sm-6">{install.processingStatus}</dd>

        <dt className="col-sm-6">Created On</dt>
        <dd className="col-sm-6">{moment(install.createdOn).format(SIMPLE_DATE_FORMAT)}</dd>

        <dt className="col-sm-6">Updated On</dt>
        <dd className="col-sm-6">{moment(install.updatedOn).format(SIMPLE_DATE_FORMAT)}</dd>
      </dl>
    ) : null;
  };

  renderSearchGroups = (group, index) => {
    const { params, parentParams, groupParams, parentGroupParams } = group;
    const hasParams = params && params.length > 0;
    const hasParentParams = parentParams && parentParams.length > 0;
    const hasGroupParams = groupParams && groupParams.length > 0;
    const hasParentGroupParams = parentGroupParams && parentGroupParams.length > 0;

    return (
      <section key={index} className="group-wrap">
        <h6>
          {index > 0 ? <span>OR</span> : null} Selection Group {index > 0 ? index + 1 : null}
        </h6>
        <div className="selected-param-group">
          <div>
            <table className="table">
              <tbody>{hasParams ? params.map(param => this.renderParams(param)) : null}</tbody>
            </table>

            {hasGroupParams ? (
              <div>
                <i>The following parameters are ORed:</i>
                <table className="table">
                  <tbody>{groupParams.map(param => this.renderParams(param))}</tbody>
                </table>
              </div>
            ) : null}

            <div className="parent-params">
              {hasParentParams ? <strong>Company Parameters</strong> : null}
              {hasParentParams ? (
                <table className="table">
                  <tbody>{parentParams.map(param => this.renderParams(param))}</tbody>
                </table>
              ) : null}

              {hasParentGroupParams ? (
                <div>
                  <i>The following parameters are ORed:</i>
                  <table className="table">
                    <tbody>{parentGroupParams.map(param => this.renderParams(param))}</tbody>
                  </table>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </section>
    );
  };

  renderParams = ({ name, displayName, displayValue }) => (
    <tr key={name}>
      <th>{displayName || name}</th>
      <td style={{ width: '100%' }}>{displayValue}</td>
    </tr>
  );

  renderClientAdmin = () => {
    const {
      order: { person },
    } = this.props;
    const fullName = `${person.firstName} ${person.lastName}`;

    return (
      <div className="col-sm-6 pull-right">
        <h6 className="text-uppercase text-muted">Client Admin</h6>
        <address>
          <strong>{fullName}</strong>
          <br />
          {person.phone}
          <br />
          {person.email}
        </address>
      </div>
    );
  };

  renderPrematchedValue = () => {
    const { order, hasEditPerms, isSuperAdmin } = this.props;

    if (order.matchedPairsProvided === 'UPLOAD_PENDING') {
      return hasEditPerms || isSuperAdmin ? (
        <span key="0">
          <Link to={`orders/order/upload-prematched/${order.id}`}>
            <i className="text-warning fa fa-ellipsis-h" /> Upload Prematched Pairs
          </Link>
        </span>
      ) : (
        <span key="0">
          <i className="text-warning fa fa-ellipsis-h" /> Upload Pending
        </span>
      );
    } else if (order.matchedPairsProvided === 'UPLOAD_COMPLETE') {
      return 'Upload Complete';
    }
    return 'Not Required';
  };

  renderEditOrderDialog = () => {
    const {
      creditsRemaining,
      dialog,
      handleSubmit,
      order,
      pristine,
      submitting,
      updateOrderFunction,
    } = this.props;
    const initialCredits = order.recordCredits;
    const creditsUsed = initialCredits - creditsRemaining;
    const updatedCreditState = this.state.updatedCredits || 0;
    const updatedCreditsRemaining = updatedCreditState - creditsUsed;
    const initialSeats = order.numberOfSeats;

    const renderCreditsInfo = (
      <span>
        {this.renderFieldAndLabel('Initial Credits', addCommas(updatedCreditState))}
        {this.renderFieldAndLabel('- Credits Used', addCommas(creditsUsed))}
        <hr />
        <div className={updatedCreditsRemaining < 0 ? 'error' : ''}>
          {this.renderFieldAndLabel('Credits Remaining', addCommas(updatedCreditsRemaining))}
        </div>
        <br />
      </span>
    );

    const renderEditCredits = (
      <fieldset>
        <Field
          name="credits"
          component={InputField}
          type="number"
          placeholder="total credits"
          label="Total Credits"
          autoFocus
          onChange={evt => {
            this.setState({ updatedCredits: evt.target.value });
          }}
        />
        <Field name="creditsUsed" component="input" type="hidden" />
      </fieldset>
    );

    const renderEditSeats = (
      <fieldset>
        <Field
          name="seats"
          component={InputField}
          type="number"
          placeholder="number of seats"
          label="Number of Seats"
          onChange={evt => {
            this.setState({ updatedNumberOfSeats: evt.target.value });
          }}
        />
        <Field name="initialSeats" component="input" type="hidden" />
      </fieldset>
    );

    const renderEditEndDate = (
      <fieldset>
        <Field
          name="endDate"
          component={InputField}
          type="date"
          label="End Date"
          onChange={evt => {
            this.setState({ updatedEndDate: evt.target.value });
          }}
        />
        <Field name="initialEndDate" component="input" type="hidden" />
        <Field name="initialStartDate" component="input" type="hidden" />
      </fieldset>
    );

    const renderEditConfiguration = (
      <div>
        {this.renderCustomConfigurationRadio('updatedCsvDownloadEnabled', 'CSV Download Enabled')}
        {this.renderCustomConfigurationRadio('updatedLeadMatchEnabled', 'Lead Match & Append Enabled')}
        {this.renderCustomConfigurationRadio('updatedContactMatchByAccount', 'Match Contacts within Matched Account Only')}
        {this.renderCustomConfigurationRadio('updatedPreferEndUserCredentials', 'Create Records Using Client Credentials')}
      </div>
    );

    const dialogContent = (
      <div className="pre-scrollable edit-order">
        <div className="container">
          {initialCredits ? renderCreditsInfo : ''}
          <Form
            id="editOrderForm"
            onSubmit={handleSubmit(params => {
              this.leaveAndResetForm();
              updateOrderFunction(params);
            })}
          >
            {initialCredits ? renderEditCredits : ''}
            {initialSeats > 0 ? renderEditSeats : ''}
            {renderEditEndDate}
            {renderEditConfiguration}
            <div className="form-buttons">
              <p className="pull-right">
                <button
                  type="button"
                  className="btn btn-secondary btn-back"
                  onClick={this.leaveAndResetForm}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={checkButtonDisabled(pristine, submitting)}
                >
                  Update Order
                </button>
              </p>
            </div>
          </Form>
          <br />
        </div>
      </div>
    );

    return (
      <DialogForm
        cancelFunction={this.leaveAndResetForm}
        content={dialogContent}
        id={EDIT_ORDER_DIALOG}
        show={dialog.show}
        title="Edit Order"
      />
    );
  };

  renderResetInstallDialog = () => {
    const { dialog, handleSubmit, submitting, updateInstallFunction } = this.props;
    const status = 'PENDING';
    const dialogContent = (
      <div className="container">
        Are you sure you want to reset the installation status to {status}?
        <Form
          id="resetInstallForm"
          onSubmit={handleSubmit(() => {
            this.toggleResetInstallDialog();
            updateInstallFunction(status);
          })}
        >
          <div className="form-buttons">
            <p className="pull-right">
              <button
                type="button"
                className="btn btn-secondary btn-back"
                onClick={this.toggleResetInstallDialog}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={checkButtonDisabled(submitting)}
              >
                Reset Installation
              </button>
            </p>
          </div>
        </Form>
        <br />
      </div>
    );

    return (
      <DialogForm
        cancelFunction={this.toggleResetInstallDialog}
        content={dialogContent}
        id={RESET_INSTALL_DIALOG}
        show={dialog.show}
        title="Reset Installation"
      />
    );
  };

  renderResendInstallEmailDialog = () => {
    const { dialog, handleSubmit, submitting, useCases, resendInstallEmailFunction } = this.props;
    // Don't allow editing email address for Customer Portal platforms
    const showEditEmailAddress =
      useCases.platformCode !== 'MARKETO' && useCases.platformCode !== 'ELOQUA';
    const renderEmailAddress = (
      <fieldset>
        <Field
          name="emailAddress"
          component={InputField}
          type="text"
          label="Email Address"
          onChange={evt => {
            this.setState({ updatedEmailAddress: evt.target.value });
          }}
        />
      </fieldset>
    );

    const dialogContent = (
      <div className="container">
        Are you sure you want to resend the installation email?
        <Form
          id="resendInstallEmailForm"
          onSubmit={handleSubmit(params => {
            this.toggleResendInstallEmailDialog();
            resendInstallEmailFunction(params);
          })}
        >
          {showEditEmailAddress ? renderEmailAddress : ''}
          <div className="form-buttons">
            <p className="pull-right">
              <button
                type="button"
                className="btn btn-secondary btn-back"
                onClick={this.leaveAndResetForm}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={checkButtonDisabled(submitting)}
              >
                Resend Installation Email
              </button>
            </p>
          </div>
        </Form>
        <br />
      </div>
    );

    return (
      <DialogForm
        cancelFunction={this.toggleResendInstallEmailDialog}
        content={dialogContent}
        id={RESEND_INSTALL_EMAIL_DIALOG}
        show={dialog.show}
        title="Resend Installation Email"
      />
    );
  };

  render() {
    const { install, dialog, isSuperAdmin, order, hasEditPerms } = this.props;
    const isOrderLoading = order === undefined || order.size === 0;
    const status = isOrderLoading ? '' : getOrderStatus(order.endDate);
    const isInstalled = install.installStatus === 'INSTALLED';

    return isOrderLoading ? null : (
      <div className="modal-wrap">
        <div className="container-fluid container-home">
          <div className="header">
            <div className="header-body align-items-center">
              <div className="row">
                <div className="col-sm-12">
                  <span className="pull-right">
                    {(hasEditPerms || isSuperAdmin) && isInstalled ? (
                      <button
                        type="button"
                        className="btn btn-danger-outline"
                        onClick={this.toggleResetInstallDialog}
                      >
                        Reset Install
                      </button>
                    ) : null}
                    &nbsp;
                    {hasEditPerms || isSuperAdmin ? (
                      <button
                        type="button"
                        className="btn btn-primary-outline"
                        onClick={this.toggleResendInstallEmailDialog}
                      >
                        Email Installation
                      </button>
                    ) : null}
                    {isInstalled ? (
                      <Link className="btn btn-primary" to={`/order-reports/${order.id}`}>
                        Report
                      </Link>
                    ) : null}
                    {isInstalled ? (
                      <Link className="btn btn-primary" to={`/matched-pair-report/${order.id}`}>
                        Export Match Ids
                      </Link>
                    ) : null}
                    {(order.recordCredits && hasEditPerms) || isSuperAdmin ? (
                      <button
                        type="button"
                        className="btn btn-primary-outline"
                        onClick={this.toggleEditOrderDialog}
                      >
                        Edit
                      </button>
                    ) : null}
                    &nbsp;
                    <button
                      type="button"
                      className="btn btn-primary-outline pull-right"
                      onClick={this.printPage}
                    >
                      Print
                    </button>
                  </span>
                  <h1 className="header-title">
                    <Link to={isSuperAdmin ? '/superadmin/orders' : '/orders/'}>
                      <i className="fa fa-angle-left" /> Orders
                    </Link>
                  </h1>
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-sm-8">
              <div className="card-border">
                <div className="card-header">
                  <div className="card-header-title">
                    Order Number: {order.providerOrderNumber} &nbsp;
                    <i className={`text-${status.color} fa fa-circle`} />
                    &nbsp;
                    {status.text}
                  </div>
                </div>
                <div className="card-block">
                  <div className="row">
                    {this.renderCustomerDetails()}
                    {this.renderClientAdmin()}
                  </div>
                  <hr />
                  <h6 className="text-uppercase text-muted">Order Detail</h6>
                  {this.renderOrderDetails()}
                  <hr />
                  <h6 className="text-uppercase text-muted">Data Detail</h6>
                  {this.renderSkuFields()}
                  <hr />
                  <h6 className="text-uppercase text-muted">Use Cases</h6>
                  {this.renderUseCases()}
                  <hr />
                  <h6 className="text-uppercase text-muted">Installation</h6>
                  {this.renderInstallationDetails()}
                  <hr />
                  {isSuperAdmin ? <h6 className="text-uppercase text-muted">Admin</h6> : null}
                  {isSuperAdmin ? this.renderSuperAdmin() : null}
                </div>
              </div>
            </div>
          </div>
        </div>
        {dialog.id === EDIT_ORDER_DIALOG && dialog.show ? this.renderEditOrderDialog() : null}
        {dialog.id === RESET_INSTALL_DIALOG && dialog.show ? this.renderResetInstallDialog() : null}
        {dialog.id === RESEND_INSTALL_EMAIL_DIALOG && dialog.show
          ? this.renderResendInstallEmailDialog()
          : null}
      </div>
    );
  }
}
